/**
 * MODAL: TOKENS INSUFFISANTS V2
 * Guide intelligent selon le plan de l'utilisateur
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, TrendingUp, Crown, Rocket, Star } from 'lucide-react';

interface InsufficientTokensModalProps {
 isOpen: boolean;
 onClose: () => void;
 currentBalance: number;
 requiredTokens: number;
 subscriptionTier?: 'free' | 'pro' | 'business' | 'enterprise';
}

export default function InsufficientTokensModal({
 isOpen,
 onClose,
 currentBalance,
 requiredTokens,
 subscriptionTier = 'free',
}: InsufficientTokensModalProps) {
 const navigate = useNavigate();

 if (!isOpen) return null;

 const missing = requiredTokens - currentBalance;

 // Recommandations intelligentes selon le plan
 const getRecommendations = () => {
 if (subscriptionTier === 'free') {
 return {
 title: 'Passez à Pro pour plus de tokens !',
 subtitle: 'Débloquez des analyses illimitées',
 primaryAction: { label: 'Upgrade vers Pro (29€/mois)', route: '/billing', icon: Crown, color: 'from-green-500 to-emerald-600' },
 secondaryAction: { label: 'Acheter des tokens', route: '/billing', icon: Zap },
 plans: [
 { name: 'Pro', price: '29€/mois', tokens: '960K tokens', features: ['Memory Pattern', 'Agents arrière-plan', 'Rollover +20%'] },
 { name: 'Pay-as-you-go', price: '20€-500€', tokens: 'Jusqu\'à 10.8M', features: ['Tokens à vie', 'Sans engagement'] }
 ]
 };
 } else if (subscriptionTier === 'pro') {
 return {
 title: 'Besoin de plus de puissance ?',
 subtitle: 'Passez à Business pour 3x plus de tokens',
 primaryAction: { label: 'Upgrade vers Business (79€/mois)', route: '/billing', icon: Rocket, color: 'from-orange-600 to-indigo-600' },
 secondaryAction: { label: 'Acheter des tokens', route: '/billing', icon: Zap },
 plans: [
 { name: 'Business', price: '79€/mois', tokens: '2.6M tokens', features: ['Big Data Analytics', 'Support prioritaire', 'Rollover +20%'] },
 { name: 'Pay-as-you-go', price: '50€-500€', tokens: 'Jusqu\'à 10.8M', features: ['Complément idéal', 'Sans engagement'] }
 ]
 };
 } else if (subscriptionTier === 'business') {
 return {
 title: 'Passez à Enterprise !',
 subtitle: 'Le maximum de tokens et fonctionnalités',
 primaryAction: { label: 'Upgrade vers Enterprise (199€/mois)', route: '/billing', icon: Star, color: 'from-purple-500 to-pink-600' },
 secondaryAction: { label: 'Acheter des tokens', route: '/billing', icon: Zap },
 plans: [
 { name: 'Enterprise', price: '199€/mois', tokens: '6.6M tokens', features: ['Multi-utilisateurs', 'API Access', 'Support 24/7'] },
 { name: 'Pay-as-you-go', price: '100€-500€', tokens: 'Jusqu\'à 10.8M', features: ['Complément idéal', 'Sans engagement'] }
 ]
 };
 } else {
 return {
 title: 'Tokens insuffisants',
 subtitle: 'Rechargez pour continuer',
 primaryAction: { label: 'Acheter des tokens', route: '/billing', icon: Zap, color: 'from-purple-600 dark:from-purple-500 to-indigo-600 dark:to-indigo-500' },
 secondaryAction: { label: 'Voir les plans', route: '/billing', icon: Crown },
 plans: [
 { name: 'Pay-as-you-go', price: '20€-500€', tokens: 'Jusqu\'à 10.8M', features: ['Tokens à vie', 'Sans engagement'] }
 ]
 };
 }
 };

 const recommendations = getRecommendations();
 const PrimaryIcon = recommendations.primaryAction.icon;
 const SecondaryIcon = recommendations.secondaryAction.icon;

 const handlePrimaryAction = () => {
 navigate(recommendations.primaryAction.route);
 onClose();
 };

 const handleSecondaryAction = () => {
 navigate(recommendations.secondaryAction.route);
 onClose();
 };

 return (
 <div className="fixed inset-0 z-[9999] flex items-center justify-center">
 {/* Backdrop */}
 <div 
 className="absolute inset-0 bg-theme-base/80 backdrop-blur-sm"
 onClick={onClose}
 />

 {/* Modal */}
 <div className="relative max-w-lg w-full mx-4 bg-gradient-to-br bg-theme-surface via-neutral-950 to-black border border-purple-600 dark:border-purple-400/30 rounded-2xl shadow-2xl shadow-orange-500/20 overflow-hidden">
 {/* Header avec gradient */}
 <div className="relative h-32 bg-gradient-to-r from-purple-600 dark:from-purple-500/20 to-red-500/20 border-b border-purple-600 dark:border-purple-400/20">
 <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDIwIDAgTCAwIDAgMCAyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwxNTUsMCwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
 
 {/* Close button */}
 <button
 onClick={onClose}
 className="absolute top-4 right-4 p-2 hover:bg-theme-surface rounded-lg transition-colors"
 >
 <X size={20} className="text-theme-secondary hover:text-theme-primary" />
 </button>

 {/* Icon central */}
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="relative">
 <div className="absolute inset-0 bg-purple-600 dark:bg-purple-500/30 blur-3xl rounded-full animate-pulse" />
 <div className="relative bg-gradient-to-br from-purple-600 dark:from-purple-500 to-indigo-600 dark:to-indigo-500 p-4 rounded-2xl shadow-lg">
 <Zap size={32} className="text-theme-primary" />
 </div>
 </div>
 </div>
 </div>

 {/* Content */}
 <div className="p-8">
 <h2 className="text-2xl font-bold text-theme-primary mb-2 text-center">
 {recommendations.title}
 </h2>
 <p className="text-theme-secondary text-sm mb-6 text-center">
 {recommendations.subtitle}
 </p>

 {/* Balance info */}
 <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-600 dark:from-purple-500/10 to-red-500/10 border border-purple-600 dark:border-purple-400/20 text-center">
 <div className="text-sm text-theme-secondary mb-1">Tokens disponibles</div>
 <div className="text-3xl font-bold text-theme-primary mb-1">
 {currentBalance.toLocaleString('fr-FR')}
 </div>
 <div className="flex items-center justify-center space-x-2 text-purple-600 dark:text-purple-400 text-sm">
 <TrendingUp size={16} />
 <span>Il vous manque {missing.toLocaleString('fr-FR')} tokens</span>
 </div>
 </div>

 {/* Plan recommendations */}
 <div className="space-y-3 mb-6">
 {recommendations.plans.map((plan, idx) => (
 <div
 key={idx}
 className="p-4 rounded-xl bg-theme-surface/50 border border-theme-border hover:border-purple-600 dark:border-purple-400/30 transition-colors"
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="text-theme-primary font-semibold mb-1">{plan.name}</div>
 <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-2">{plan.tokens}</div>
 <ul className="space-y-1">
 {plan.features.map((feature, i) => (
 <li key={i} className="text-xs text-theme-secondary flex items-center">
 <span className="mr-2"></span>
 {feature}
 </li>
 ))}
 </ul>
 </div>
 <div className="text-right">
 <div className="text-xl font-bold text-theme-primary">{plan.price}</div>
 </div>
 </div>
 </div>
 ))}
 </div>

 {/* Actions */}
 <div className="space-y-3">
 <button
 onClick={handlePrimaryAction}
 className={`w-full px-6 py-3 bg-gradient-to-r ${recommendations.primaryAction.color} text-theme-primary font-bold rounded-xl transition-all shadow-lg hover:scale-105 flex items-center justify-center space-x-2`}
 >
 <PrimaryIcon size={20} />
 <span>{recommendations.primaryAction.label}</span>
 </button>
 <button
 onClick={handleSecondaryAction}
 className="w-full px-6 py-3 bg-theme-hover hover:bg-neutral-700 text-theme-primary font-semibold rounded-xl transition-all flex items-center justify-center space-x-2"
 >
 <SecondaryIcon size={18} />
 <span>{recommendations.secondaryAction.label}</span>
 </button>
 <button
 onClick={onClose}
 className="w-full px-6 py-2 text-theme-secondary hover:text-theme-primary transition-colors text-sm"
 >
 Annuler
 </button>
 </div>

 <p className="text-xs text-theme-tertiary mt-4 text-center">
 Paiement sécurisé par Stripe • Satisfaction garantie
 </p>
 </div>
 </div>
 </div>
 );
}

